"use client";

import { useMemo } from "react";
import { usePreOrderFlows as useOfflinePreOrderFlows } from "@stockline/web";
import type { PreOrderFlow as LegacyPreOrderFlow } from "@/app/api/types/preOrder";
import {
  mapOfflineFlowToLegacy,
  sortOfflineFlows,
} from "../utils/mapOfflineFlowToLegacy";

/**
 * Per-pre-order flows subscription, replacing `preOrder.flows` from the
 * central recap pipeline as the source of truth for everything a single
 * `PreOrderSection` renders.
 *
 * Why this exists: the central `offlineShim` pipeline re-fetches all flows
 * for the active date scope on every Dexie write — a 4,000+ row indexed
 * scan that fires twice per save (reconcile commit + SSE replay) and used
 * to fire three times before optimistic writes were dropped. With this
 * hook, `PreOrderSection` only re-renders when its own pre-order's flows
 * change in IndexedDB; cross-PO writes wake the live-query but the indexed
 * scan filtered by `preOrderId` returns the same rows and React.memo on
 * FlowRow bails. Per-PO scan is ~0.1 ms vs the central ~700 ms.
 *
 * Shape: returns the legacy snake-case `PreOrderFlow` shape downstream
 * cells already understand, with `product`/`unit`/`partner` left as
 * `undefined`. Cells already resolve those from the recap-wide products /
 * units / partners context caches (RecapContext + global Dexie
 * subscriptions), so the lean shape is sufficient.
 *
 * Synthetic ids: AI-proposal virtual pre-orders carry ids like
 * `proposal-<batchId>` / `proposal-shell-<batchId>` (see
 * `buildVirtualPreOrder` / `buildShellFromFlows`). They never appear in
 * `preOrderFlows`, so we skip the subscription entirely — without this,
 * every proposal section would register its own dead live query that
 * re-fires on any flow write. Callers should source flows from the prop
 * (`preOrder.flows`, baked from `batch.proposals`) in that case.
 */
const PROPOSAL_ID_PREFIX = "proposal-";

export function useLiveFlowsForPreOrder(
  preOrderId: string,
): LegacyPreOrderFlow[] {
  const skip = preOrderId.startsWith(PROPOSAL_ID_PREFIX);
  const offlineFlows = useOfflinePreOrderFlows({ preOrderId, skip });
  return useMemo(
    () => sortOfflineFlows(offlineFlows).map(mapOfflineFlowToLegacy),
    [offlineFlows],
  );
}
